// Tầng I/O của `avatar sync --all` (#211 phase-02): đọc đĩa cho từng entry registry rồi
// giao dữ kiện cho hàm quyết định THUẦN (`plan-sync-all-across-registered-workspaces.ts`).
//
// Tách khỏi hàm quyết định vì đúng một lý do đo được: 5 luật lọc + positive control cần
// unit test, mà dựng workspace thật cho mỗi tổ hợp luật thì test đắt và giòn. Ở đây chỉ có
// đọc file — không có luật nào.
import { stat } from "node:fs/promises";
import { join } from "node:path";
import { detectOpenClaudeCodeSession } from "./detect-open-claude-code-session.js";
import { pathExists } from "./filesystem-helpers.js";
import { readPackVersionFromFile } from "./pack-manifest-store.js";
import type { WorkspaceFacts } from "./plan-sync-all-across-registered-workspaces.js";
import { TEAM_PACK_RELATIVE_PATH } from "./team-pack-manager.js";
import { WORKSPACE_MARKER_RELATIVE } from "./workspace-marker-constants.js";
import type { WorkspaceEntry } from "./workspace-registry-store.js";

/** mtime `.claude/pack` — mốc #3 của luật stale. Không stat được ⇒ `null` (KHÔNG phải 0). */
async function packMtimeMs(workspaceRoot: string): Promise<number | null> {
  try {
    return (await stat(join(workspaceRoot, TEAM_PACK_RELATIVE_PATH))).mtimeMs;
  } catch {
    return null;
  }
}

/**
 * Gom dữ kiện cho MỘT entry.
 *
 * Marker mất ⇒ workspace đã bị xoá/di chuyển: BỎ QUA mọi phép đọc còn lại (thư mục không
 * còn thì `.claude/pack` cũng không) và để hàm quyết định xếp vào `pruned`.
 */
export async function collectWorkspaceFacts(
  entry: WorkspaceEntry,
  homeDir: string,
  now: number,
): Promise<WorkspaceFacts> {
  const markerExists = await pathExists(join(entry.path, WORKSPACE_MARKER_RELATIVE));
  if (!markerExists) {
    return {
      entry,
      markerExists: false,
      currentVersion: null,
      packMtimeMs: null,
      session: { status: "none", evidence: "workspace không còn trên đĩa", newestMtimeMs: null },
    };
  }

  const [currentVersion, mtime] = await Promise.all([
    readPackVersionFromFile(join(entry.path, TEAM_PACK_RELATIVE_PATH)).catch(() => null),
    packMtimeMs(entry.path),
  ]);

  return {
    entry,
    markerExists: true,
    currentVersion,
    packMtimeMs: mtime,
    // Một lượt `readdir + stat` DUY NHẤT phục vụ cả luật open-session lẫn mốc #2 của luật
    // stale — đó là lý do bản port trả thêm `newestMtimeMs` thay vì chỉ `{status, evidence}`.
    session: detectOpenClaudeCodeSession(entry.path, homeDir, now),
  };
}

export async function collectAllWorkspaceFacts(
  entries: readonly WorkspaceEntry[],
  homeDir: string,
  now: number,
): Promise<WorkspaceFacts[]> {
  return await Promise.all(entries.map((e) => collectWorkspaceFacts(e, homeDir, now)));
}
